import { useContext, useMemo } from 'react';
import { Chess } from 'chess.js';
import GameContext from '../context/GameContext';
import { GameMoveModel } from '../types/GamesModel';
import { MoveType } from '../types/MoveType';
import { getIcon } from '../utils/ChessTypes';
import { mateLabel, whiteWinPct } from './winChance';

// One ply of a best line: SAN, an optional move-number prefix ("21." / "21…"), and its index for
// replaying the line up to here.
export type BestLinePly = { san: string; numberLabel: string | null; index: number };
// A best line ready to render and replay: start position, raw UCI moves and per-ply display data.
// Capped to stay glanceable.
export type BestLine = { baseFen: string; uci: string[]; plies: BestLinePly[] };

function buildBestLine(fen: string, pv: string, maxPlies = 6): BestLine | null {
  const tokens = pv.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return null;
  }
  try {
    const chess = new Chess(fen);
    const plies: BestLinePly[] = [];
    const uci: string[] = [];
    for (let i = 0; i < Math.min(tokens.length, maxPlies); i++) {
      const t = tokens[i];
      if (t.length < 4) {
        break;
      }
      const moveNumber = chess.moveNumber();
      const white = chess.turn() === 'w';
      const mv = chess.move({
        from: t.slice(0, 2),
        to: t.slice(2, 4),
        promotion: t.length > 4 ? t.slice(4, 5) : undefined,
      });
      if (mv == null) {
        break;
      }
      uci.push(t);
      // Number white moves ("21."); a line opening on Black's turn gets the "21…" marker.
      const numberLabel = white ? `${moveNumber}.` : i === 0 ? `${moveNumber}…` : null;
      plies.push({ san: mv.san, numberLabel, index: i });
    }
    return plies.length > 0 ? { baseFen: fen, uci, plies } : null;
  } catch {
    return null;
  }
}

// Per-move analysis of GameContext.currentMove (win-chance swing from the viewer's side,
// classification, the engine's best move at the prior position), shared by the docked panel and the
// mobile view.
export function useMoveInsights() {
  const { game, currentMove, orientation } = useContext(GameContext);

  const movesModel = useMemo(
    () => game.Moves.flatMap((m) => [m.White, m.Black]).filter(Boolean) as GameMoveModel[],
    [game.Moves],
  );

  // The swing from the viewer's side (the bottom of the board), not the mover's: viewing as White, a
  // Black blunder shows White's chance climbing.
  const winSwing = useMemo(() => {
    if (currentMove == null) {
      return null;
    }
    const idx = movesModel.findIndex((m) => m.Pgn === currentMove.Pgn);
    const prev = idx > 0 ? movesModel[idx - 1] : null;
    const prevEval = prev != null ? prev.Evaluation : 0;
    const whiteNow = whiteWinPct(currentMove.Evaluation, currentMove.MateIn);
    const whitePrev = whiteWinPct(prevEval, prev?.MateIn);
    const viewerNow = orientation === 'white' ? whiteNow : 100 - whiteNow;
    const viewerPrev = orientation === 'white' ? whitePrev : 100 - whitePrev;
    return Math.round(viewerNow - viewerPrev);
  }, [currentMove, movesModel, orientation]);

  // The best move before this one is the previous half-move's BestMove; ParentBestMove isn't populated.
  const parentBestMove = useMemo(() => {
    if (currentMove == null) {
      return null;
    }
    const idx = movesModel.findIndex((m) => m.Pgn === currentMove.Pgn);
    return idx > 0 ? movesModel[idx - 1].BestMove : null;
  }, [currentMove, movesModel]);

  // The position this move was played from: the previous half-move's FEN, or the start position. Seeds
  // the "try another move" what-if.
  const parentFen = useMemo(() => {
    if (currentMove == null) {
      return null;
    }
    const idx = movesModel.findIndex((m) => m.Pgn === currentMove.Pgn);
    if (idx > 0) {
      return movesModel[idx - 1].Fen;
    }
    return 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
  }, [currentMove, movesModel]);

  // The same best move as SAN for the readout. Null when the played move was itself the best.
  const bestMoveSan = useMemo(() => {
    if (currentMove == null || parentBestMove == null || parentBestMove.length < 4) {
      return null;
    }
    const idx = movesModel.findIndex((m) => m.Pgn === currentMove.Pgn);
    if (idx <= 0) {
      return null;
    }
    try {
      const chess = new Chess(movesModel[idx - 1].Fen);
      const mv = chess.move({
        from: parentBestMove.slice(0, 2),
        to: parentBestMove.slice(2, 4),
        promotion: parentBestMove.length > 4 ? parentBestMove.slice(4, 5) : undefined,
      });
      return mv == null || mv.san === currentMove.NewPosition ? null : mv.san;
    } catch {
      return null;
    }
  }, [currentMove, movesModel, parentBestMove]);

  // The engine line for error-family moves, gated on classification rather than the viewer's swing (an
  // opponent's blunder is still worth punishing):
  //   * Miss: the parent position's line, opening with the move that should have been played.
  //   * otherwise: this move's own line, how the game best continues / the mistake is refuted.
  // Null for non-errors, before evaluation, or when the PV can't be replayed.
  const bestLine = useMemo(() => {
    if (currentMove == null) {
      return null;
    }
    const mt = currentMove.MoveType;
    const isMistake =
      mt === MoveType.Inaccuracy ||
      mt === MoveType.Mistake ||
      mt === MoveType.Miss ||
      mt === MoveType.Blunder;
    if (!isMistake) {
      return null;
    }
    if (currentMove.MoveType === MoveType.Miss) {
      const idx = movesModel.findIndex((m) => m.Pgn === currentMove.Pgn);
      const parent = idx > 0 ? movesModel[idx - 1] : null;
      if (parent?.Fen == null || parent.Pv == null) {
        return null;
      }
      return buildBestLine(parent.Fen, parent.Pv);
    }
    if (currentMove.Fen == null || currentMove.Pv == null) {
      return null;
    }
    return buildBestLine(currentMove.Fen, currentMove.Pv);
  }, [currentMove, movesModel]);

  const moveTypeLabel = currentMove?.MoveType != null ? MoveType[currentMove.MoveType] : null;
  // "M3" / "-M3" on a forced mate, from the viewer's side like the bar. Null otherwise.
  const mate = useMemo(() => {
    const n = currentMove?.MateIn;
    if (n == null || n === 0) {
      return null;
    }
    return mateLabel(orientation === 'white' ? n : -n);
  }, [currentMove?.MateIn, orientation]);
  const EvalIcon = getIcon(currentMove?.MoveType ?? undefined);
  const viewerName = orientation === 'white' ? 'White' : 'Black';
  // A Miss's line starts from the parent position, so the panel labels it "Instead:".
  const bestLineFromParent = currentMove?.MoveType === MoveType.Miss;

  return {
    winSwing,
    mate,
    parentBestMove,
    parentFen,
    bestMoveSan,
    bestLine,
    bestLineFromParent,
    moveTypeLabel,
    EvalIcon,
    viewerName,
  };
}
